import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { dequal } from "dequal";
import { DateTime } from "luxon";

export interface UserSelectionState {
  base: string;
  statTest: string;
  periodType: string;
  periodStartDate: string;
  periodEndDate: string;
  period: string;
  postPeriodType: string;
  postStartDate: string;
  postEndDate: string;
  mode:
    | "POINT_IN_TIME"
    | "TREND"
    | "PRE_POST"
    | "BUTTERFLY"
    | "PROFILE"
    | "PERCEPTUAL"
    | "DIFFERENTIATION";
  brands: number[]; // Multiple brands as array
  secondaryBrands: number[];
  markets: number[]; // Multiple markets as array
  segments: number[]; // Only plural key, no singular key
  activeSegments: number[];
  filters: number[];
  groupedBy: string;
  questionOptions: string[];
  visualizationType: string;
  ageRange: number[] | null;
  scoreType: string;
  profileMetric: number | null;
  profileGroup: number | null;
  profileResponse: number | null;
  trendsMetric: string | null;
  trendsResponse: string | null;
  viewId: string | null;
  hiddenSegments?: number[];
  hiddenBrands?: number[];
  screenType: string;
  hiddenMarkets?: number[];
  hiddenQuestions?: string[];
}

const initialState: UserSelectionState = {
  base: "GENPOP",
  statTest: "POP",
  periodType: "L180D",
  screenType: "",
  mode: "POINT_IN_TIME",
  periodStartDate: DateTime.now().minus({ days: 180 }).toFormat("yyyy-MM-dd"),
  periodEndDate: DateTime.now().minus({ days: 1 }).toFormat("yyyy-MM-dd"),
  period: "MV_12",
  postPeriodType: "L180D",
  postStartDate: DateTime.now().minus({ days: 180 }).toFormat("yyyy-MM-dd"),
  postEndDate: DateTime.now().minus({ days: 1 }).toFormat("yyyy-MM-dd"),
  visualizationType: "",
  groupedBy: "RESPONSE",
  brands: [], // Multiple brands as array
  secondaryBrands: [],
  markets: [], // Multiple markets as array
  segments: [], // Only plural key, no singular key
  activeSegments: [],
  filters: [],
  questionOptions: [],
  ageRange: null,
  scoreType: "MEAN",
  profileGroup: null,
  profileMetric: null,
  profileResponse: null,
  trendsMetric: null,
  trendsResponse: null,
  viewId: null,
  hiddenSegments: [],
  hiddenBrands: [],
  hiddenMarkets: [],
};

interface UpdateFieldPayload {
  field: keyof UserSelectionState;
  value: string | string[];
}

export const userSelectionSlice = createSlice({
  name: "userSelection",
  initialState,
  reducers: {
    updateSelection: (
      state,
      action: PayloadAction<Partial<UserSelectionState>>
    ) => {
      // Update multiple fields at once
      return { ...state, ...action.payload };
    },
    updateField: (state, action: PayloadAction<UpdateFieldPayload>) => {
      // Update a single field
      const { field, value } = action.payload;
      (state as any)[field] = value;
    },
    resetSelection: () => {
      return initialState;
    },
    syncFromURL: (
      state: UserSelectionState,
      action: PayloadAction<Partial<UserSelectionState>>
    ) => {
      Object.entries(action.payload).forEach(([key, value]: [string, any]) => {
        //@ts-ignore
        if (!dequal(state[key], value)) {
          //@ts-ignore
          state[key] = value;
        }
      });
      return state;
    },
  },
});

export const { updateSelection, updateField, resetSelection, syncFromURL } =
  userSelectionSlice.actions;

export default userSelectionSlice.reducer;
